// bd-deepcontext-b4zw — the list pipeline lifted out of ScheduledJobs.tsx:
// load-order sort, bd-sh26v isolation, bd-yj2sq search, expired-sink display
// sort and bd-uhi3b client-side pagination. All pure; the page just renders the
// result.
import type { PaginationInfo } from "../../types";
import { isJobExpired } from "./jobStatus";
import type { ScheduledJob } from "./types";

export const JOBS_PER_PAGE = 10;

// Load order: active jobs first, then newest-created first.
export const sortJobsByActivity = <
  T extends Pick<ScheduledJob, "isActive" | "createdAt">,
>(
  jobs: T[],
): T[] =>
  [...jobs].sort((a, b) => {
    if (a.isActive !== b.isActive) {
      return b.isActive ? 1 : -1;
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

// bd-yj2sq — a job matches if its title OR any referenced blueprint NAME
// contains the term (case-insensitive). Blueprint names come from the resolved
// name map (bd-wxgk9); ids not yet resolved are treated as non-matching, and the
// job id is never searched.
export const filterJobsBySearch = (
  jobs: ScheduledJob[],
  search: string,
  blueprintNames: Record<string, string>,
): ScheduledJob[] => {
  const trimmed = search.trim().toLowerCase();
  if (!trimmed) return jobs;
  return jobs.filter((job) => {
    if (job.name?.toLowerCase().includes(trimmed)) return true;
    return (job.promptIds || []).some((id) => {
      const name = blueprintNames[id];
      return !!name && name.toLowerCase().includes(trimmed);
    });
  });
};

// bd-sh26v — exact-id isolation takes priority over the text search: when a
// jobId is pinned via the URL, show only that one job (or nothing if it is gone).
export const selectVisibleJobs = ({
  jobs,
  isolatedJobId,
  search,
  blueprintNames,
}: {
  jobs: ScheduledJob[];
  isolatedJobId: string;
  search: string;
  blueprintNames: Record<string, string>;
}): { isolatedJob?: ScheduledJob; filteredJobs: ScheduledJob[] } => {
  if (isolatedJobId) {
    const isolatedJob = jobs.find((job) => job.id === isolatedJobId);
    return { isolatedJob, filteredJobs: isolatedJob ? [isolatedJob] : [] };
  }
  return {
    isolatedJob: undefined,
    filteredJobs: filterJobsBySearch(jobs, search, blueprintNames),
  };
};

// bd-uhi3b — expired jobs sink to the bottom; everything else keeps its
// incoming order (Array#sort is stable).
export const sortJobsForDisplay = (jobs: ScheduledJob[]): ScheduledJob[] =>
  [...jobs].sort((a, b) => {
    const aExpired = isJobExpired(a);
    const bExpired = isJobExpired(b);
    if (aExpired && !bExpired) return 1;
    if (!aExpired && bExpired) return -1;
    return 0;
  });

export const buildPaginationInfo = ({
  totalCount,
  page,
  limit,
}: {
  totalCount: number;
  page: number;
  limit: number;
}): PaginationInfo => {
  const totalPages = Math.ceil(totalCount / limit);
  return {
    page,
    limit,
    totalCount,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
    nextPage: page < totalPages ? page + 1 : null,
    previousPage: page > 1 ? page - 1 : null,
  };
};

export const paginateJobs = (
  jobs: ScheduledJob[],
  page: number,
  limit: number,
): ScheduledJob[] => jobs.slice((page - 1) * limit, page * limit);
